import { Component } from '@theme/component';

/**
 * Purchase controls on a collection product card: pack, delivery frequency and
 * the two buy buttons.
 *
 * ## This component shows and hides; it never computes
 *
 * Every price the card can display — each pack against each plan, plus the
 * per-serving figure for all of them — is already in the DOM, rendered by
 * Liquid through the store's own money filters. All this does is decide which
 * of them is visible.
 *
 * That is not a stylistic preference. Formatting money in JavaScript means
 * reimplementing the store's currency format, its separators and its locale,
 * and getting all three right forever. Deriving a subscription price in
 * JavaScript additionally means reimplementing selling-plan discounts, which
 * Shopify already resolved server-side in `variant.selling_plan_allocations`.
 *
 * ## The frequency select is the form field
 *
 * There is no hidden `selling_plan` input mirroring the select — the select is
 * named `selling_plan` itself. "Try it once" disables it on the way into the
 * submit, and a disabled control is not submitted, so that line reaches the
 * cart without a plan while "Subscribe & Save" keeps it. One element, one
 * source of truth, nothing to keep in step.
 *
 * ## No refs, on purpose
 *
 * These controls live inside `<product-form-component>`, which is itself a
 * `Component`. Refs resolve to the closest Component ancestor, so a `ref=` on
 * any of them would land on the product form instead of here — and would
 * collide with the `variantId` ref it genuinely owns. Querying by class
 * sidesteps that, and the markup's `on:` handlers name this tag for the same
 * reason.
 *
 * @extends {Component}
 */
export default class EmberkCardPurchase extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#apply();
  }

  /** A re-render (a filter change, say) hands back fresh elements. */
  updatedCallback() {
    super.updatedCallback();
    this.#apply();
  }

  selectPack = () => this.#apply();

  selectPlan = () => this.#apply();

  /**
   * Click runs before submit, so flipping the select here decides what the
   * form about to be sent actually contains.
   */
  buySubscription = () => {
    const select = this.#planSelect;
    if (select) select.disabled = false;
  };

  buyOnce = () => {
    const select = this.#planSelect;
    if (select) select.disabled = true;
  };

  /** @returns {HTMLSelectElement | null} */
  get #planSelect() {
    return this.querySelector('.emberk-card-buy__select');
  }

  /**
   * Reads the controls and reconciles everything to them. Both handlers route
   * here rather than each doing its own partial update — that is what stops the
   * price, the per-serving figure and the submitted variant from drifting apart.
   */
  #apply() {
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantInput) return;

    // A single-variant product has no pack radios, so fall back to the variant
    // the server already put in the form.
    const pack = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.emberk-card-buy__pack-input:checked')
    );
    const variant = pack?.value ?? variantInput.value;
    const plan = this.#planSelect?.value ?? '';

    variantInput.value = variant;
    this.dataset.variant = variant;
    this.dataset.plan = plan;

    for (const figure of this.querySelectorAll('.emberk-card-buy__figure')) {
      if (!(figure instanceof HTMLElement)) continue;

      const figurePlan = figure.dataset.plan ?? '';

      // "any" marks a figure that depends only on the pack — the one-time price
      // in the second button, which is the same whichever frequency is chosen.
      const matchesPlan = figurePlan === 'any' || figurePlan === plan;

      figure.hidden = figure.dataset.variant !== variant || !matchesPlan;
    }
  }
}

if (!customElements.get('emberk-card-purchase-component')) {
  customElements.define('emberk-card-purchase-component', EmberkCardPurchase);
}
